import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, RefreshCw, X, XCircle } from "lucide-react"
import { NavLink } from "react-router-dom"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import "./controlling.css"

/**
 * Controlling — echte Zahlen aus der Buchhaltung (Paul 25.10.2026).
 *
 * Quelle sind die Kontenblaetter der Erfolgskonten in BuchhaltungsButler, also
 * dieselben Nettobetraege wie in BWA und Abschluss. Die Seite rechnet nichts
 * dazu, was die Buchhaltung nicht kennt:
 *
 *  - **IST und Forecast sind optisch getrennt.** Forecast-Zellen sind kursiv
 *    und schraffiert, Forecast-Balken ebenso. Wer eine Zahl abliest, sieht, ob
 *    sie gebucht oder erwartet ist.
 *  - **Jede Hochrechnung nennt ihre Methode** (Konto-Zeile, rechts neben dem
 *    Namen) — eine Prognose ohne Herkunft ist eine Behauptung.
 *  - **Datenstand und Pruefungen stehen sichtbar oben bzw. unten.** Schlug der
 *    letzte Abruf fehl, sagt ein roter Kasten, dass der gezeigte Stand aelter ist.
 */

type MonatInfo = { monat: string; status: "ist" | "vorlaeufig" | "laufend" | "offen"; offene_belege: boolean }
type Werte = {
  ist: (number | null)[]
  fc: (number | null)[]
  vj: number[]
  ytd: number
  fc_jahr: number
  fc_jahr_6: number
  vj_jahr: number
  vj_ytd: number
}
type Annahme = { id: number; art: "endet" | "betrag"; ab_monat: string; betrag: number | null; notiz: string; von: string }
type Lieferant = Werte & { schluessel: string; name: string; methode: string; annahmen: Annahme[] }
type Konto = Werte & { konto: string; name: string; methode: string; lieferanten: Lieferant[] }
type OffenerPosten = {
  monat: string; konto: string; konto_name: string; gruppe: string; art: "ertrag" | "aufwand"
  lieferant: string; erwartet: number; gebucht: number; offen: number; methode: string
}
type AbgleichMonat = {
  leistungsmonat: string; rechnungsmonat: string; gerechnet: number | null; gebucht: number | null
  status: "abgeglichen" | "abweichung" | "fehlt" | "offen" | "keine" | "ohne_rechnung" | "aufladung" | "ohne_aufladung"
}
type AbgleichAnbieter = {
  anbieter: string; label: string; versatz: number; monate: AbgleichMonat[]; offen_gerechnet: number
  kumuliert: null | { seit: string; aufgeladen: number; verbraucht: number; guthaben_rechnerisch: number }
}
type Gruppe = Werte & { schluessel: string; label: string; art: "ertrag" | "aufwand"; konten: Konto[] }
type Kennzahl = Werte & { schluessel: string; label: string }
type Pruefung = { name: string; ok: boolean; text: string }
type Stand = {
  konfiguriert: boolean
  letzter_lauf: null | { status: string; gestartet: string; beendet: string | null; fehler: string | null; ausloeser: string | null }
  datenstand: string | null
  alter_stunden: number | null
  veraltet: boolean
  pruefungen: Pruefung[]
  kontenrahmen: string | null
}
type Antwort = {
  stand: Stand
  auswertung: null | {
    jahr: number
    kontenrahmen: string
    monate: MonatInfo[]
    basis_monate: string[]
    gruppen: Gruppe[]
    kennzahlen: Kennzahl[]
    offene_posten: OffenerPosten[]
    nachtraege?: { id: number; monat: string; konto: string; lieferant: string; betrag: number; notiz: string; von: string; auch_gebucht: boolean }[]
  }
  abgleich?: {
    anbieter: AbgleichAnbieter[]
    hinweise: string[]
    fixkosten: Record<string, { betrag: number; stand: string }>
  }
  datenqualitaet: null | {
    unverbuchte_belege: { monat: string; richtung: string; anzahl: number; brutto: number }[]
    nicht_festgeschrieben: { monat: string; anzahl: number }[]
  }
}

const MON = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]
const MONAT_LANG = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August",
  "September", "Oktober", "November", "Dezember"]

const eur0 = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
const eur2 = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
const zahl0 = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 })

function kurz(v: number): string {
  const a = Math.abs(v)
  if (a >= 1_000_000) return `${(v / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Mio`
  if (a >= 10_000) return `${(v / 1000).toLocaleString("de-DE", { maximumFractionDigits: 0 })} T`
  if (a >= 1000) return `${(v / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} T`
  return zahl0.format(v)
}

function zeitLabel(iso: string | null): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
}

/** Prozentuale Veraenderung; null wenn die Basis 0 ist (keine Division durch nichts). */
function delta(neu: number, alt: number): number | null {
  if (Math.abs(alt) < 0.5) return null
  return ((neu - alt) / Math.abs(alt)) * 100
}

function Delta({ neu, alt, mehrIstGut }: { neu: number; alt: number; mehrIstGut: boolean }) {
  const d = delta(neu, alt)
  if (d === null) return <span className="ctl-delta">kein Vorjahreswert</span>
  const gut = mehrIstGut ? d >= 0 : d <= 0
  return (
    <span className={`ctl-delta ${gut ? "gut" : "schlecht"}`}>
      {d >= 0 ? "▲" : "▼"} {Math.abs(d).toLocaleString("de-DE", { maximumFractionDigits: 1 })} % zum Vorjahr
    </span>
  )
}

/** Wert einer Monatszelle: gebucht, sonst Forecast. */
function zelle(w: Werte, i: number, st: MonatInfo["status"]): { wert: number | null; fc: boolean } {
  if (st === "ist") return { wert: w.ist[i], fc: false }
  return { wert: w.fc[i], fc: true }
}

// ── Diagramme ─────────────────────────────────────────────────────────────────

/** Breite des Containers in Pixeln. Die Diagramme zeichnen in echter Breite
 *  statt ein festes viewBox hochzuskalieren — sonst wachsen Schrift und Hoehe
 *  mit, sobald die Karten untereinander stehen. */
function useBreite(start: number): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null)
  const [breite, setBreite] = useState(start)
  useEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver(([e]) => setBreite(Math.max(280, Math.round(e.contentRect.width))))
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])
  return [ref, breite]
}

function MonatsBalken({ monate, erl, kos }: { monate: MonatInfo[]; erl: Werte; kos: Werte }) {
  const [hover, setHover] = useState<number | null>(null)
  const [ref, B] = useBreite(640)
  const H = 240, L = 46, R = 8, T = 10, U = 24
  const werte = monate.map((m, i) => ({
    e: zelle(erl, i, m.status).wert ?? 0,
    k: zelle(kos, i, m.status).wert ?? 0,
    fc: m.status !== "ist",
  }))
  const max = Math.max(1, ...werte.map((w) => Math.max(w.e, w.k)))
  const schritt = Math.pow(10, Math.floor(Math.log10(max)))
  const oben = Math.ceil(max / schritt) * schritt
  const y = (v: number) => T + (H - T - U) * (1 - v / oben)
  const breite = (B - L - R) / 12
  const bw = Math.max(3, Math.min(16, breite / 2 - 4))
  const ticks = [0, oben / 2, oben]

  return (
    <div className="ctl-chart" ref={ref} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${B} ${H}`} width={B} height={H} role="img" aria-label="Erlöse und Kosten je Monat">
        <defs>
          {[["erl", "var(--ctl-erl)"], ["kos", "var(--ctl-kos)"]].map(([id, farbe]) => (
            <pattern key={id} id={`ctl-fc-${id}`} width="5" height="5" patternUnits="userSpaceOnUse"
                     patternTransform="rotate(45)">
              <rect width="5" height="5" fill={farbe} opacity="0.22" />
              <line x1="0" y1="0" x2="0" y2="5" stroke={farbe} strokeWidth="2.2" />
            </pattern>
          ))}
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={B - R} y1={y(t)} y2={y(t)} stroke="var(--ctl-grid)" />
            <text x={L - 6} y={y(t) + 4} fontSize="10.5" textAnchor="end" fill="var(--ctl-ink-3)">{kurz(t)}</text>
          </g>
        ))}
        {werte.map((w, i) => {
          const x0 = L + i * breite + breite / 2
          return (
            <g key={i}>
              {hover === i && <rect x={L + i * breite + 1} y={T} width={breite - 2} height={H - T - U} fill="#f2f1ec" rx="4" />}
              {([["erl", w.e, x0 - bw - 1], ["kos", w.k, x0 + 1]] as const).map(([id, v, x]) => {
                const h = Math.max(0, y(0) - y(v))
                const r = Math.min(4, h / 2, bw / 2)
                // Oben gerundet, unten auf der Grundlinie gerade.
                const d = `M${x},${y(0)} V${y(v) + r} Q${x},${y(v)} ${x + r},${y(v)} H${x + bw - r} Q${x + bw},${y(v)} ${x + bw},${y(v) + r} V${y(0)} Z`
                return h > 0 ? (
                  <path key={id} d={d} fill={w.fc ? `url(#ctl-fc-${id})` : id === "erl" ? "var(--ctl-erl)" : "var(--ctl-kos)"} />
                ) : null
              })}
              <text x={x0} y={H - 7} fontSize="10.5" textAnchor="middle"
                    fill={w.fc ? "var(--ctl-ink-3)" : "var(--ctl-ink-2)"}>{MON[i]}</text>
              <rect x={L + i * breite} y={0} width={breite} height={H} fill="transparent"
                    onMouseEnter={() => setHover(i)} />
            </g>
          )
        })}
        <line x1={L} x2={B - R} y1={y(0)} y2={y(0)} stroke="var(--ctl-ink-3)" />
      </svg>
      {hover !== null && (
        <div className="ctl-tip" style={{ left: `${((L + hover * breite + breite / 2) / B) * 100}%`, top: `${(y(Math.max(werte[hover].e, werte[hover].k)) / H) * 100}%` }}>
          <div><b>{MONAT_LANG[hover]}</b><span>{werte[hover].fc ? "Forecast" : "IST"}</span></div>
          <div><span>Erlöse</span><b>{eur0.format(werte[hover].e)}</b></div>
          <div><span>Kosten</span><b>{eur0.format(werte[hover].k)}</b></div>
          <div><span>Ergebnis</span><b>{eur0.format(werte[hover].e - werte[hover].k)}</b></div>
        </div>
      )}
    </div>
  )
}

function Kumuliert({ monate, k }: { monate: MonatInfo[]; k: Kennzahl }) {
  const [hover, setHover] = useState<number | null>(null)
  const [ref, B] = useBreite(440)
  const H = 240, L = 50, R = 10, T = 12, U = 24
  const ist: (number | null)[] = []
  const fc: (number | null)[] = []
  const vj: number[] = []
  let summe = 0, summeVj = 0
  let letzterIst = -1
  monate.forEach((m, i) => {
    const z = zelle(k, i, m.status)
    summe += z.wert ?? 0
    summeVj += k.vj[i] ?? 0
    vj.push(summeVj)
    if (!z.fc) { ist.push(summe); fc.push(null); letzterIst = i }
    else { ist.push(null); fc.push(summe) }
  })
  // Der Forecast setzt am letzten IST-Punkt an, damit die Linie nicht springt.
  if (letzterIst >= 0 && letzterIst < 11) fc[letzterIst] = ist[letzterIst]
  const alle = [...ist, ...fc, ...vj].filter((v): v is number => v !== null)
  const lo = Math.min(0, ...alle), hi = Math.max(1, ...alle)
  const spanne = hi - lo || 1
  const x = (i: number) => L + ((B - L - R) / 11) * i
  const y = (v: number) => T + (H - T - U) * (1 - (v - lo) / spanne)
  const pfad = (r: (number | null)[]) =>
    r.map((v, i) => (v === null ? null : `${x(i)},${y(v)}`)).filter(Boolean).join(" L")

  return (
    <div className="ctl-chart" ref={ref} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${B} ${H}`} width={B} height={H} role="img" aria-label={`${k.label} kumuliert`}>
        {[lo, (lo + hi) / 2, hi].map((t, j) => (
          <g key={j}>
            <line x1={L} x2={B - R} y1={y(t)} y2={y(t)} stroke="var(--ctl-grid)" />
            <text x={L - 6} y={y(t) + 4} fontSize="10.5" textAnchor="end" fill="var(--ctl-ink-3)">{kurz(t)}</text>
          </g>
        ))}
        {lo < 0 && <line x1={L} x2={B - R} y1={y(0)} y2={y(0)} stroke="var(--ctl-ink-3)" />}
        <path d={`M${pfad(vj)}`} fill="none" stroke="var(--ctl-vj)" strokeWidth="2" />
        {fc.some((v) => v !== null) && (
          <path d={`M${pfad(fc)}`} fill="none" stroke="var(--ctl-erg)" strokeWidth="2" strokeDasharray="5 4" />
        )}
        {ist.some((v) => v !== null) && (
          <path d={`M${pfad(ist)}`} fill="none" stroke="var(--ctl-erg)" strokeWidth="2" />
        )}
        {letzterIst >= 0 && (
          <circle cx={x(letzterIst)} cy={y(ist[letzterIst] as number)} r="4" fill="var(--ctl-erg)"
                  stroke="#fff" strokeWidth="2" />
        )}
        {MON.map((m, i) => (
          <text key={m} x={x(i)} y={H - 7} fontSize="10.5" textAnchor="middle" fill="var(--ctl-ink-3)">
            {i % 2 === 0 ? m : ""}
          </text>
        ))}
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - U} stroke="var(--ctl-ink-3)" strokeDasharray="2 3" />}
        {MON.map((_, i) => (
          <rect key={i} x={x(i) - (B - L - R) / 22} y={0} width={(B - L - R) / 11} height={H}
                fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null && (
        <div className="ctl-tip" style={{ left: `${(x(hover) / B) * 100}%`, top: `${(T / H) * 100 + 8}%` }}>
          <div><b>bis Ende {MONAT_LANG[hover]}</b></div>
          <div><span>{ist[hover] !== null ? "IST" : "Forecast"}</span><b>{eur0.format((ist[hover] ?? fc[hover]) as number)}</b></div>
          <div><span>Vorjahr</span><b>{eur0.format(vj[hover])}</b></div>
        </div>
      )}
    </div>
  )
}

// ── Seite ─────────────────────────────────────────────────────────────────────

/** Reiter oben rechts: Uebersicht | Annahmen (Unterseite seit 25.10.2026). */
export function ControllingReiter() {
  return (
    <nav className="ctl-segment" aria-label="Controlling-Bereiche">
      <NavLink end to="/controlling" className={({ isActive }) => isActive ? "aktiv" : ""}>Übersicht</NavLink>
      <NavLink to="/controlling/annahmen" className={({ isActive }) => isActive ? "aktiv" : ""}>Planung</NavLink>
    </nav>
  )
}

const REIHENFOLGE: { typ: "gruppe" | "kennzahl"; schluessel: string }[] = [
  { typ: "gruppe", schluessel: "erloese" },
  { typ: "gruppe", schluessel: "material" },
  { typ: "kennzahl", schluessel: "rohertrag" },
  { typ: "gruppe", schluessel: "personal" },
  { typ: "gruppe", schluessel: "raum" },
  { typ: "gruppe", schluessel: "versicherungen" },
  { typ: "gruppe", schluessel: "kfz" },
  { typ: "gruppe", schluessel: "werbung_reise" },
  { typ: "gruppe", schluessel: "abschreibungen" },
  { typ: "gruppe", schluessel: "sonstige" },
  { typ: "kennzahl", schluessel: "betriebsergebnis" },
  { typ: "gruppe", schluessel: "neutral_ertrag" },
  { typ: "gruppe", schluessel: "neutral_aufwand" },
  { typ: "kennzahl", schluessel: "vor_steuern" },
  { typ: "gruppe", schluessel: "steuern" },
  { typ: "kennzahl", schluessel: "jahresergebnis" },
]

function summeWerte(teile: Werte[]): Werte {
  const n = (f: (w: Werte) => number) => teile.reduce((s, w) => s + f(w), 0)
  const arr = (f: (w: Werte) => (number | null)[]) =>
    Array.from({ length: 12 }, (_, i) => {
      const v = teile.map((w) => f(w)[i])
      return v.every((x) => x === null) ? null : v.reduce<number>((s, x) => s + (x ?? 0), 0)
    })
  return {
    ist: arr((w) => w.ist), fc: arr((w) => w.fc),
    vj: Array.from({ length: 12 }, (_, i) => teile.reduce((s, w) => s + (w.vj[i] ?? 0), 0)),
    ytd: n((w) => w.ytd), fc_jahr: n((w) => w.fc_jahr), fc_jahr_6: n((w) => w.fc_jahr_6),
    vj_jahr: n((w) => w.vj_jahr), vj_ytd: n((w) => w.vj_ytd),
  }
}

export default function ControllingPage() {
  const jetzt = new Date().getFullYear()
  const [jahr, setJahr] = useState(jetzt)
  const [offen, setOffen] = useState<Set<string>>(new Set())
  const [konto, setKonto] = useState<{ k: Konto; art: "ertrag" | "aufwand" } | null>(null)
  const qc = useQueryClient()

  const q = useQuery<Antwort>({
    queryKey: ["controlling", jahr],
    queryFn: async () => {
      const r = await fetch(`/api/v1/controlling?jahr=${jahr}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    // Solange ein Abruf laeuft, alle 4 s nachsehen.
    refetchInterval: (query) => (query.state.data?.stand.letzter_lauf?.status === "laeuft" ? 4000 : false),
  })

  const abruf = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/v1/controlling/abruf", { method: "POST", credentials: "include" })
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`)
    },
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ["controlling"] }), 800),
  })

  const a = q.data?.auswertung
  const stand = q.data?.stand
  const laeuft = stand?.letzter_lauf?.status === "laeuft" || abruf.isPending

  const abgeleitet = useMemo(() => {
    if (!a) return null
    const g = Object.fromEntries(a.gruppen.map((x) => [x.schluessel, x]))
    const k = Object.fromEntries(a.kennzahlen.map((x) => [x.schluessel, x]))
    const kostenGruppen = a.gruppen.filter((x) => x.art === "aufwand" && !["steuern", "neutral_aufwand"].includes(x.schluessel))
    return { g, k, kosten: summeWerte(kostenGruppen) }
  }, [a])

  const istMonate = a?.monate.filter((m) => m.status === "ist") ?? []
  const bis = istMonate.length ? MONAT_LANG[Number(istMonate[istMonate.length - 1].monat.slice(5)) - 1] : null

  return (
    <div className="ctl" style={{ padding: "20px 24px" }}>
      <div className="ctl-kopf">
        <div>
          <h1 className="ctl-titel">Controlling</h1>
          <div className="ctl-unter">
            Gewinn- und Verlustrechnung aus BuchhaltungsButler{a ? ` · ${a.kontenrahmen}` : ""}
            {bis ? ` · IST bis ${bis}` : ""} · netto
          </div>
        </div>
        <div className="ctl-aktionen">
          <ControllingReiter />
          {stand && (
            <span className="ctl-stand" title={stand.datenstand ? `Letzter vollständiger Abruf ${zeitLabel(stand.datenstand)}` : ""}>
              <span className={`ctl-punkt ${stand.letzter_lauf?.status === "fehler" ? "fehler" : stand.veraltet ? "alt" : ""}`} />
              {laeuft ? "Abruf läuft …" : stand.datenstand ? `Stand ${zeitLabel(stand.datenstand)}` : "noch kein Abruf"}
            </span>
          )}
          <div className="ctl-segment" role="group" aria-label="Geschäftsjahr">
            {[jetzt - 1, jetzt].map((j) => (
              <button key={j} aria-pressed={jahr === j} onClick={() => setJahr(j)}>{j}</button>
            ))}
          </div>
          <button className="ctl-knopf" disabled={laeuft || !stand?.konfiguriert} onClick={() => abruf.mutate()}>
            <RefreshCw size={14} className={laeuft ? "animate-spin" : ""} /> Jetzt abrufen
          </button>
        </div>
      </div>

      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error as Error} />}
      {abruf.error && (
        <div className="ctl-banner fehler"><XCircle size={16} /><div>{(abruf.error as Error).message}</div></div>
      )}

      {stand && !stand.konfiguriert && (
        <div className="ctl-banner warn">
          <AlertTriangle size={16} />
          <div><strong>BuchhaltungsButler ist noch nicht verbunden.</strong> Auf dem Server fehlen
            <code> BHB_API_CLIENT</code>, <code>BHB_API_SECRET</code> und <code>BHB_API_KEY</code>.</div>
        </div>
      )}
      {stand?.letzter_lauf?.status === "fehler" && (
        <div className="ctl-banner fehler">
          <XCircle size={16} />
          <div>
            <strong>Letzter Abruf ({zeitLabel(stand.letzter_lauf.gestartet)}) verworfen:</strong> {stand.letzter_lauf.fehler}
            <br />{stand.datenstand
              ? <>Gezeigt wird der letzte vollständige Stand vom {zeitLabel(stand.datenstand)} — nichts davon ist halb übernommen.</>
              : <>Es gibt noch keinen vollständigen Stand.</>}
          </div>
        </div>
      )}
      {stand && stand.datenstand && stand.veraltet && stand.letzter_lauf?.status !== "fehler" && (
        <div className="ctl-banner warn">
          <AlertTriangle size={16} />
          <div><strong>Daten älter als einen Tag</strong> ({zeitLabel(stand.datenstand)}). Der Abruf läuft sonst alle 6 Stunden.</div>
        </div>
      )}

      {q.data && !a && stand?.konfiguriert && !laeuft && (
        <div className="ctl-karte ctl-leer">Noch kein vollständiger Abruf. „Jetzt abrufen“ holt Vorjahr und laufendes Jahr (1–2 Minuten).</div>
      )}

      {a && abgeleitet && (
        <>
          <div className="ctl-kpis">
            <Kpi label={`Umsatz ${bis ? `Jan–${MON[istMonate.length - 1]}` : "lfd. Jahr"}`} farbe="var(--ctl-erl)"
                 wert={abgeleitet.g.erloese.ytd} alt={abgeleitet.g.erloese.vj_ytd} mehrIstGut />
            <Kpi label="Betriebliche Kosten" farbe="var(--ctl-kos)"
                 wert={abgeleitet.kosten.ytd} alt={abgeleitet.kosten.vj_ytd} mehrIstGut={false} />
            <Kpi label="Ergebnis vor Steuern" farbe="var(--ctl-erg)"
                 wert={abgeleitet.k.vor_steuern.ytd} alt={abgeleitet.k.vor_steuern.vj_ytd} mehrIstGut />
            <div className="ctl-kpi" style={{ ["--akzent" as string]: "#6250d6" }}>
              <div className="ctl-kpi-label">Forecast Ergebnis vor Steuern {a.jahr}</div>
              <div className={`ctl-kpi-wert ${abgeleitet.k.vor_steuern.fc_jahr < 0 ? "ctl-neg" : ""}`}>
                {eur0.format(abgeleitet.k.vor_steuern.fc_jahr)}
              </div>
              <div className="ctl-kpi-fuss">
                <span>Spanne {eur0.format(Math.min(abgeleitet.k.vor_steuern.fc_jahr, abgeleitet.k.vor_steuern.fc_jahr_6))}
                  {" – "}{eur0.format(Math.max(abgeleitet.k.vor_steuern.fc_jahr, abgeleitet.k.vor_steuern.fc_jahr_6))}</span>
                <span>Vorjahr {eur0.format(abgeleitet.k.vor_steuern.vj_jahr)}</span>
              </div>
            </div>
          </div>

          <div className="ctl-reihe">
            <div className="ctl-karte">
              <div className="ctl-karte-kopf">
                <h3>Erlöse und Kosten je Monat</h3>
                <div className="ctl-legende">
                  <span><i className="ctl-swatch" style={{ background: "var(--ctl-erl)" }} />Erlöse</span>
                  <span><i className="ctl-swatch" style={{ background: "var(--ctl-kos)" }} />Betriebliche Kosten</span>
                  <span><i className="ctl-swatch" style={{ background: "repeating-linear-gradient(135deg,#8a8880 0 2px,#e3e2dc 2px 4px)" }} />Forecast</span>
                </div>
              </div>
              <MonatsBalken monate={a.monate} erl={abgeleitet.g.erloese} kos={abgeleitet.kosten} />
            </div>
            <div className="ctl-karte">
              <div className="ctl-karte-kopf">
                <h3>Ergebnis vor Steuern, kumuliert</h3>
                <div className="ctl-legende">
                  <span><i className="ctl-strich" style={{ borderColor: "var(--ctl-erg)" }} />{a.jahr}</span>
                  <span><i className="ctl-strich gestrichelt" style={{ borderColor: "var(--ctl-erg)" }} />Forecast</span>
                  <span><i className="ctl-strich" style={{ borderColor: "var(--ctl-vj)" }} />{a.jahr - 1}</span>
                </div>
              </div>
              <Kumuliert monate={a.monate} k={abgeleitet.k.vor_steuern} />
            </div>
          </div>

          <Monatsstand monate={a.monate} gruppen={a.gruppen} posten={a.offene_posten} />

          <div className="ctl-karte-kopf" style={{ marginTop: 6 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Gewinn- und Verlustrechnung {a.jahr}</h3>
            <span className="ctl-klein">
              Kursiv/schraffiert = Forecast · Klick auf eine Gruppe öffnet die Konten, Klick auf ein Konto das Kontenblatt
            </span>
          </div>
          <div className="ctl-tabelle-huelle">
            <table className="ctl-tabelle">
              <thead>
                <tr>
                  <th>Position</th>
                  {a.monate.map((m, i) => (
                    <th key={m.monat} className={m.status !== "ist" ? "ctl-fc" : ""}
                        title={m.status === "vorlaeufig" ? "Wird noch nachgebucht — zählt nicht zur Forecast-Basis" : m.status === "laufend" ? "Laufender Monat" : ""}>
                      {MON[i]}{m.status === "vorlaeufig" ? "*" : ""}
                      {m.offene_belege && <span className="ctl-markierung" title="Belege ohne Buchung in diesem Monat" />}
                    </th>
                  ))}
                  <th className="trenner">IST lfd.</th>
                  <th>VJ gleich. Zr.</th>
                  <th>Δ</th>
                  <th className="trenner">Forecast {a.jahr}</th>
                  <th>Vorjahr</th>
                </tr>
              </thead>
              <tbody>
                {REIHENFOLGE.map((z) => {
                  if (z.typ === "kennzahl") {
                    const k = abgeleitet.k[z.schluessel]
                    return <Zeile key={z.schluessel} klasse={`kennzahl ${z.schluessel === "jahresergebnis" || z.schluessel === "vor_steuern" ? "haupt" : ""}`}
                                  label={k.label} w={k} vz={1} monate={a.monate} mehrIstGut />
                  }
                  const g = abgeleitet.g[z.schluessel]
                  if (!g || (!g.konten.length && z.schluessel !== "erloese")) return null
                  const vz = g.art === "aufwand" ? -1 : 1
                  const auf = offen.has(g.schluessel)
                  return [
                    <Zeile key={g.schluessel} klasse="gruppe" monate={a.monate} w={g} vz={vz} mehrIstGut={g.art === "ertrag"}
                           label={<>{auf ? <ChevronDown size={14} style={{ flexShrink: 0 }} /> : <ChevronRight size={14} style={{ flexShrink: 0 }} />}{g.label}
                                   <span className="ctl-methode">{g.konten.length} {g.konten.length === 1 ? "Konto" : "Konten"}</span></>}
                           onClick={() => setOffen((s) => { const n = new Set(s); if (n.has(g.schluessel)) n.delete(g.schluessel); else n.add(g.schluessel); return n })} />,
                    ...(auf ? g.konten.map((k) => (
                      <Zeile key={`${g.schluessel}-${k.konto}`} klasse="konto" monate={a.monate} w={k} vz={vz}
                             mehrIstGut={g.art === "ertrag"} onClick={() => setKonto({ k, art: g.art })}
                             label={<span title={`${k.konto} ${k.name} — Forecast: ${k.methode}`}>{k.konto} {k.name}
                               <span className="ctl-methode">{k.methode}</span></span>} />
                    )) : []),
                  ]
                })}
              </tbody>
            </table>
          </div>

          {q.data?.abgleich && q.data.abgleich.anbieter.length > 0 && <Abgleich daten={q.data.abgleich} />}

          <Datenqualitaet stand={stand!} dq={q.data!.datenqualitaet} jahr={a.jahr} basis={a.basis_monate} nachtraege={a.nachtraege ?? []} />
        </>
      )}

      {konto && a && <Kontenblatt konto={a.gruppen.flatMap((g) => g.konten).find((x) => x.konto === konto.k.konto) ?? konto.k}
                                   art={konto.art} jahr={a.jahr} onClose={() => setKonto(null)} />}
    </div>
  )
}

function Kpi({ label, wert, alt, mehrIstGut, farbe }: { label: string; wert: number; alt: number; mehrIstGut: boolean; farbe: string }) {
  return (
    <div className="ctl-kpi" style={{ ["--akzent" as string]: farbe }}>
      <div className="ctl-kpi-label">{label}</div>
      <div className={`ctl-kpi-wert ${wert < 0 ? "ctl-neg" : ""}`}>{eur0.format(wert)}</div>
      <div className="ctl-kpi-fuss">
        <Delta neu={wert} alt={alt} mehrIstGut={mehrIstGut} />
        <span>VJ {eur0.format(alt)}</span>
      </div>
    </div>
  )
}

function Zeile({ label, w, vz, monate, klasse, onClick, mehrIstGut }: {
  label: React.ReactNode; w: Werte; vz: number; monate: MonatInfo[]; klasse: string
  onClick?: () => void; mehrIstGut: boolean
}) {
  const f = (v: number | null | undefined) => {
    if (v === null || v === undefined) return ""
    const x = v * vz
    if (Math.abs(x) < 0.5) return "–"
    return <span className={x < 0 && klasse.includes("kennzahl") ? "ctl-neg" : ""}>{zahl0.format(x)}</span>
  }
  const d = delta(w.ytd, w.vj_ytd)
  const gut = d === null ? null : (mehrIstGut ? d >= 0 : d <= 0)
  return (
    <tr className={klasse} onClick={onClick}>
      <td>{label}</td>
      {monate.map((m, i) => {
        const z = zelle(w, i, m.status)
        const titel = m.status === "laufend" || m.status === "vorlaeufig"
          ? `bisher gebucht ${eur0.format((w.ist[i] ?? 0) * vz)}` : undefined
        return <td key={m.monat} className={z.fc ? "ctl-fc" : ""} title={titel}>{f(z.wert)}</td>
      })}
      <td className="trenner">{f(w.ytd)}</td>
      <td>{f(w.vj_ytd)}</td>
      <td className={gut === null ? "" : gut ? "" : "ctl-neg"} style={gut ? { color: "var(--ctl-gut)" } : undefined}>
        {d === null ? "" : `${d >= 0 ? "+" : ""}${d.toLocaleString("de-DE", { maximumFractionDigits: 0 })} %`}
      </td>
      <td className="trenner ctl-fc" style={{ fontWeight: 600 }}>{f(w.fc_jahr)}</td>
      <td>{f(w.vj_jahr)}</td>
    </tr>
  )
}

/** Laufender (und noch nachgebuchter) Monat: was ist gebucht, was kommt noch.
 *  Die Liste ist genau das, was der Forecast fuer diese Monate zusaetzlich zum
 *  Gebuchten annimmt — damit man sieht, worauf die Zahl beruht. */
function Monatsstand({ monate, gruppen, posten }: { monate: MonatInfo[]; gruppen: Gruppe[]; posten: OffenerPosten[] }) {
  const qc = useQueryClient()
  // „Kommt nicht mehr" mit einem Klick: Annahme „endet ab diesem Monat".
  const endet = useMutation({
    mutationFn: async (p: OffenerPosten) => {
      const r = await fetch("/api/v1/controlling/annahmen", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ konto: p.konto, lieferant_name: p.lieferant, art: "endet", ab_monat: p.monat,
          notiz: "Im Monatsstand als „kommt nicht mehr“ markiert" }),
      })
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["controlling"] })
      qc.invalidateQueries({ queryKey: ["controlling-annahmen"] })
    },
  })
  const relevant = monate.map((m, i) => ({ ...m, i })).filter((m) => m.status === "laufend" || m.status === "vorlaeufig")
  if (!relevant.length) return null
  return (
    <div className="ctl-reihe" style={{ gridTemplateColumns: relevant.length > 1 ? "1fr 1fr" : "1fr" }}>
      {relevant.map((m) => {
        const summe = (art: "ertrag" | "aufwand", feld: "ist" | "fc") =>
          gruppen.filter((g) => g.art === art && g.schluessel !== "steuern").reduce((s, g) => s + (g[feld][m.i] ?? 0), 0)
        const offen = posten.filter((p) => p.monat === m.monat)
        const offenKosten = offen.filter((p) => p.art === "aufwand").reduce((s, p) => s + p.offen, 0)
        const offenErl = offen.filter((p) => p.art === "ertrag").reduce((s, p) => s + p.offen, 0)
        return (
          <div className="ctl-karte" key={m.monat}>
            <div className="ctl-karte-kopf">
              <h3>{MONAT_LANG[m.i]} {m.monat.slice(0, 4)} — {m.status === "laufend" ? "laufender Monat" : "wird noch nachgebucht"}</h3>
            </div>
            <div className="ctl-monatsstand">
              <div><span className="ctl-klein">Erlöse gebucht</span><b>{eur0.format(summe("ertrag", "ist"))}</b>
                <span className="ctl-klein">erwartet {eur0.format(summe("ertrag", "fc"))}</span></div>
              <div><span className="ctl-klein">Kosten gebucht</span><b>{eur0.format(summe("aufwand", "ist"))}</b>
                <span className="ctl-klein">erwartet {eur0.format(summe("aufwand", "fc"))}</span></div>
              <div><span className="ctl-klein">Noch offen</span><b>{eur0.format(offenKosten)}</b>
                <span className="ctl-klein">Kosten{offenErl > 0.5 ? ` · ${eur0.format(offenErl)} Erlöse` : ""}</span></div>
            </div>
            {offen.length > 0 ? (
              <table className="ctl-blatt" style={{ marginTop: 8 }}>
                <thead><tr><th>Noch nicht gebucht</th><th>Konto</th><th className="zahl">Erwartet</th><th className="zahl">Gebucht</th><th className="zahl">Offen</th><th>Grundlage</th><th /></tr></thead>
                <tbody>{offen.map((p, j) => (
                  <tr key={j}>
                    <td>{p.lieferant}{p.art === "ertrag" && <span className="ctl-methode">Erlös</span>}</td>
                    <td className="ctl-klein">{p.konto} {p.konto_name}</td>
                    <td className="zahl">{eur2.format(p.erwartet)}</td>
                    <td className="zahl">{p.gebucht ? eur2.format(p.gebucht) : "–"}</td>
                    <td className="zahl" style={{ fontWeight: 600 }}>{eur2.format(p.offen)}</td>
                    <td className="ctl-klein">{p.methode}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="ctl-link" disabled={endet.isPending}
                              title={`Annahme „endet ab ${MON[Number(p.monat.slice(5)) - 1]}“ anlegen — in Controlling → Annahmen änderbar`}
                              onClick={() => { if (confirm(`${p.lieferant}: kommt ab ${MONAT_LANG[Number(p.monat.slice(5)) - 1]} nichts mehr?`)) endet.mutate(p) }}>
                        kommt nicht mehr
                      </button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <p className="ctl-klein" style={{ marginTop: 8 }}>Alles Erwartete ist gebucht.</p>}
          </div>
        )
      })}
    </div>
  )
}

const ABGLEICH_STATUS: Record<AbgleichMonat["status"], { text: string; farbe: string }> = {
  abgeglichen: { text: "passt", farbe: "var(--ctl-gut)" },
  abweichung: { text: "weicht ab", farbe: "#b54708" },
  fehlt: { text: "Rechnung fehlt", farbe: "var(--ctl-schlecht)" },
  offen: { text: "Rechnung kommt", farbe: "var(--ctl-ink-3)" },
  keine: { text: "", farbe: "var(--ctl-ink-3)" },
  ohne_rechnung: { text: "nur gebucht", farbe: "var(--ctl-ink-3)" },
  aufladung: { text: "Aufladung", farbe: "var(--ctl-ink-3)" },
  ohne_aufladung: { text: "keine Aufladung", farbe: "var(--ctl-ink-3)" },
}

/** Gerechnet (Portal, /kosten) gegen gebucht (Buchhaltung), je Leistungsmonat. */
function Abgleich({ daten }: { daten: NonNullable<Antwort["abgleich"]> }) {
  const monate = Array.from(new Set(daten.anbieter.flatMap((a) =>
    a.monate.filter((m) => m.status !== "keine").map((m) => m.leistungsmonat)))).sort().slice(-6)
  if (!monate.length) return null
  return (
    <div className="ctl-karte" style={{ marginTop: 14 }}>
      <div className="ctl-karte-kopf">
        <h3>Abgleich Plattformkosten: gerechnet im Portal ↔ gebucht in der Buchhaltung</h3>
        <span className="ctl-klein">je Leistungsmonat · netto</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="ctl-blatt" style={{ minWidth: 720 }}>
          <thead><tr><th>Anbieter</th>{monate.map((m) => (
            <th key={m} className="zahl">{MON[Number(m.slice(5)) - 1]} {m.slice(2, 4)}</th>))}
            <th className="zahl">Noch nicht berechnet</th></tr></thead>
          <tbody>{daten.anbieter.map((a) => (
            <tr key={a.anbieter}>
              <td><b style={{ fontWeight: 600 }}>{a.label}</b>
                <div className="ctl-klein">{a.kumuliert ? "Aufladungen, nur in Summe vergleichbar"
                  : a.versatz ? "Rechnung im Folgemonat" : "Rechnung im selben Monat"}</div>
                {a.kumuliert && (
                  <div className="ctl-klein" style={{ marginTop: 4 }}>
                    Seit {MON[Number(a.kumuliert.seit.slice(5)) - 1]} {a.kumuliert.seit.slice(2, 4)}: aufgeladen {eur2.format(a.kumuliert.aufgeladen)},
                    verbraucht (gerechnet) {eur2.format(a.kumuliert.verbraucht)} →{" "}
                    <b style={{ color: a.kumuliert.guthaben_rechnerisch < 0 ? "var(--ctl-schlecht)" : "var(--ctl-ink)" }}>
                      rechnerisches Guthaben {eur2.format(a.kumuliert.guthaben_rechnerisch)}</b>
                  </div>
                )}</td>
              {monate.map((m) => {
                const z = a.monate.find((x) => x.leistungsmonat === m)
                if (!z || z.status === "keine") return <td key={m} className="zahl">–</td>
                const st = ABGLEICH_STATUS[z.status]
                return (
                  <td key={m} className="zahl" title={`Leistung ${m}, Rechnung ${z.rechnungsmonat}`}>
                    <div>{z.gebucht !== null ? eur2.format(z.gebucht) : "—"}</div>
                    <div className="ctl-klein">gerechnet {z.gerechnet !== null ? eur2.format(z.gerechnet) : "—"}</div>
                    <div style={{ fontSize: 11, color: st.farbe, fontWeight: 600 }}>{st.text}</div>
                  </td>
                )
              })}
              <td className="zahl" style={{ fontWeight: 600 }}>{a.offen_gerechnet ? eur2.format(a.offen_gerechnet) : "–"}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <p className="ctl-klein" style={{ marginTop: 8 }}>
        Obere Zahl gebucht, darunter was das Portal selbst rechnet: AWS und Azure aus den Tokens, sipgate aus den eigenen Anrufen.
        Fixkosten sind Annahmen
        {Object.entries(daten.fixkosten).map(([k, f]) => ` · ${k} ${eur2.format(f.betrag)} (Stand ${f.stand})`).join("")}.
        „Weicht ab“ heißt mehr als 10 % und mehr als 2 € Unterschied. Dann stimmt entweder die Rechnung im Portal nicht oder die Buchung.
        {daten.hinweise.map((h) => <span key={h}><br /><AlertTriangle size={12} style={{ verticalAlign: -2 }} /> {h}</span>)}
      </p>
    </div>
  )
}

function Datenqualitaet({ stand, dq, jahr, basis, nachtraege }: {
  stand: Stand; dq: Antwort["datenqualitaet"]; jahr: number; basis: string[]
  nachtraege: NonNullable<NonNullable<Antwort["auswertung"]>["nachtraege"]>
}) {
  const [liste, setListe] = useState(false)
  const belege = useQuery<{ belege: { richtung: string; datum: string; partner: string; rechnungsnr: string; brutto: number | null }[] }>({
    queryKey: ["controlling-belege", jahr],
    enabled: liste,
    queryFn: async () => {
      const r = await fetch(`/api/v1/controlling/belege-offen?jahr=${jahr}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const unverbucht = dq?.unverbuchte_belege ?? []
  const anzahl = unverbucht.reduce((s, r) => s + r.anzahl, 0)
  const brutto = unverbucht.reduce((s, r) => s + r.brutto, 0)
  const nichtFest = (dq?.nicht_festgeschrieben ?? []).reduce((s, r) => s + r.anzahl, 0)
  const monatLabel = (m: string) => `${MON[Number(m.slice(5)) - 1]} ${m.slice(2, 4)}`

  return (
    <div className="ctl-qualitaet">
      <div className="ctl-karte">
        <div className="ctl-karte-kopf"><h3>Prüfungen beim letzten Abruf</h3>
          <span className="ctl-klein">{zeitLabel(stand.datenstand)}</span></div>
        {stand.pruefungen.map((p) => (
          <div key={p.name} className="ctl-pruefung">
            {p.ok ? <CheckCircle2 size={15} color="var(--ctl-gut)" /> : <XCircle size={15} color="var(--ctl-schlecht)" />}
            <div><strong style={{ fontWeight: 600 }}>{p.name}</strong><div className="ctl-klein">{p.text}</div></div>
          </div>
        ))}
        {nachtraege.length > 0 && (
          <div className="ctl-pruefung">
            <AlertTriangle size={15} color="#d29200" />
            <div>
              <strong style={{ fontWeight: 600 }}>
                {nachtraege.length} Nachtr{nachtraege.length === 1 ? "ag" : "äge"} außerhalb der Buchhaltung: {eur2.format(nachtraege.reduce((s, n) => s + n.betrag, 0))}
              </strong>
              <div className="ctl-klein">
                Um genau diesen Betrag weicht das Controlling von der BWA ab. {nachtraege.map((n) => `${n.lieferant} ${MON[Number(n.monat.slice(5)) - 1]} ${eur2.format(n.betrag)}`).join(" · ")}
              </div>
              {nachtraege.filter((n) => n.auch_gebucht).map((n) => (
                <div key={n.id} className="ctl-klein" style={{ color: "var(--ctl-schlecht)", fontWeight: 600 }}>
                  ⚠ {n.lieferant} {MON[Number(n.monat.slice(5)) - 1]}: inzwischen auch gebucht — Nachtrag in der Planung entfernen, sonst zählt es doppelt.
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="ctl-klein" style={{ marginTop: 10 }}>
          Schlägt eine Prüfung fehl, wird der ganze Abruf verworfen und der letzte vollständige Stand bleibt stehen.
          Forecast-Basis: {basis.map(monatLabel).join(", ") || "—"} (regelmäßige Konten: Ø der letzten 3 Monate,
          unregelmäßige wie im Vorjahresmonat; Spanne mit Ø 6 Monate).
        </p>
      </div>
      <div className="ctl-karte">
        <div className="ctl-karte-kopf"><h3>Was die Buchhaltung noch nicht enthält</h3></div>
        <div className="ctl-pruefung">
          {anzahl ? <AlertTriangle size={15} color="#d29200" /> : <CheckCircle2 size={15} color="var(--ctl-gut)" />}
          <div>
            <strong style={{ fontWeight: 600 }}>{anzahl ? `${anzahl} Belege ohne Buchung (${eur2.format(brutto)} brutto)` : "Alle Belege verbucht"}</strong>
            <div className="ctl-klein">
              {anzahl ? <>Sie fehlen in der GuV, bis sie gebucht sind. Monate: {unverbucht.map((r) => `${monatLabel(r.monat)} (${r.anzahl} ${r.richtung === "inbound" ? "Eingang" : "Ausgang"})`).join(", ")}.{" "}
                <button className="ctl-knopf" style={{ padding: "2px 8px", marginTop: 4 }} onClick={() => setListe((x) => !x)}>
                  {liste ? "Liste schließen" : "Belege anzeigen"}</button></> : "Kein hochgeladener Beleg wartet auf eine Buchung."}
            </div>
          </div>
        </div>
        {liste && belege.data && (
          <table className="ctl-blatt" style={{ marginTop: 6 }}>
            <thead><tr><th>Datum</th><th>Art</th><th>Partner</th><th>Nr.</th><th className="zahl">Brutto</th></tr></thead>
            <tbody>{belege.data.belege.map((b, i) => (
              <tr key={i}><td>{new Date(b.datum).toLocaleDateString("de-DE")}</td>
                <td>{b.richtung === "inbound" ? "Eingang" : "Ausgang"}</td><td>{b.partner}</td><td>{b.rechnungsnr}</td>
                <td className="zahl">{b.brutto === null ? "?" : eur2.format(b.brutto)}</td></tr>
            ))}</tbody>
          </table>
        )}
        <div className="ctl-pruefung">
          {nichtFest ? <AlertTriangle size={15} color="#d29200" /> : <CheckCircle2 size={15} color="var(--ctl-gut)" />}
          <div>
            <strong style={{ fontWeight: 600 }}>{nichtFest ? `${nichtFest} Buchungen noch nicht festgeschrieben` : "Alle Buchungen festgeschrieben"}</strong>
            <div className="ctl-klein">Nicht festgeschriebene Buchungen zählen schon mit, können sich aber noch ändern.</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function monatPlus(n: number): string {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() + n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
}

/** Je Lieferant/Kunde: Forecast-Grundlage und Annahmen von Hand.
 *  Was keine Statistik wissen kann — gekuendigt, neuer Vertrag, Preiserhoehung. */
function Annahmen({ konto, art: kontoArt, jahr }: { konto: Konto; art: "ertrag" | "aufwand"; jahr: number }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<{ name: string; neu: boolean; annahme?: Annahme } | null>(null)
  const [art, setArt] = useState<"endet" | "betrag">("endet")
  const [ab, setAb] = useState(monatPlus(1))
  const [betrag, setBetrag] = useState("")
  const [notiz, setNotiz] = useState("")
  const neu = useMutation({
    mutationFn: async () => {
      // Aendern legt serverseitig eine neue Fassung an; die alte bleibt im Verlauf.
      const r = await fetch(form!.annahme ? `/api/v1/controlling/annahmen/${form!.annahme.id}` : "/api/v1/controlling/annahmen", {
        method: form!.annahme ? "PUT" : "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ konto: konto.konto, lieferant_name: form!.name, art, ab_monat: ab,
          betrag: art === "betrag" ? Number(betrag.replace(",", ".")) : null, notiz }),
      })
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`)
    },
    onSuccess: () => { setForm(null); setNotiz(""); setBetrag(""); qc.invalidateQueries({ queryKey: ["controlling"] }) },
  })
  const weg = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/v1/controlling/annahmen/${id}`, { method: "DELETE", credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["controlling"] }),
  })
  const wer = kontoArt === "ertrag" ? "Kunde" : "Lieferant"
  return (
    <div style={{ margin: "8px 0 14px" }}>
      <table className="ctl-blatt">
        <thead><tr><th>{wer}</th><th className="zahl">IST lfd.</th><th className="zahl">Forecast {jahr}</th><th>Grundlage</th><th /></tr></thead>
        <tbody>{konto.lieferanten.map((l) => (
          <tr key={l.schluessel}>
            <td>{l.name}
              {l.annahmen.map((a) => (
                <div key={a.id} className="ctl-klein">
                  Annahme: {a.art === "endet" ? "endet" : eur2.format(a.betrag ?? 0) + "/Monat"} ab {a.ab_monat.slice(5)}/{a.ab_monat.slice(2, 4)}
                  {a.notiz && ` — ${a.notiz}`}{a.von && ` (${a.von})`}{" "}
                  <button className="ctl-link" onClick={() => {
                    setArt(a.art); setAb(a.ab_monat); setBetrag(a.betrag === null ? "" : String(a.betrag).replace(".", ","))
                    setNotiz(a.notiz); setForm({ name: l.name, neu: false, annahme: a })
                  }}>ändern</button>{" · "}
                  <button className="ctl-link" onClick={() => weg.mutate(a.id)}>zurücknehmen</button>
                </div>
              ))}
            </td>
            <td className="zahl">{eur2.format(l.ytd)}</td>
            <td className="zahl">{eur2.format(l.fc_jahr)}</td>
            <td className="ctl-klein">{l.methode}</td>
            <td><button className="ctl-link" onClick={() => {
              setArt("endet"); setAb(monatPlus(1)); setBetrag(""); setNotiz(""); setForm({ name: l.name, neu: false })
            }}>Annahme</button></td>
          </tr>
        ))}</tbody>
      </table>
      {!form && <button className="ctl-link" style={{ marginTop: 6 }} onClick={() => setForm({ name: "", neu: true })}>+ Neuer {wer} auf diesem Konto</button>}
      {form && (
        <div className="ctl-annahme-form">
          <div className="ctl-klein" style={{ fontWeight: 600 }}>
            {form.annahme ? "Annahme ändern" : "Annahme"} für {form.neu ? `neuen ${wer}` : form.name}
          </div>
          {form.neu && <input placeholder={`Name ${wer}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={art} onChange={(e) => setArt(e.target.value as "endet" | "betrag")}>
              <option value="endet">endet</option>
              <option value="betrag">fester Betrag je Monat (netto)</option>
            </select>
            <label className="ctl-klein">ab <input type="month" value={ab} onChange={(e) => setAb(e.target.value)} /></label>
            {art === "betrag" && <input placeholder="Betrag €" inputMode="decimal" value={betrag} onChange={(e) => setBetrag(e.target.value)} style={{ width: 110 }} />}
          </div>
          <input placeholder="Notiz (z. B. Vertrag gekündigt zum 31.10.)" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
          {neu.error && <div className="ctl-klein" style={{ color: "var(--ctl-schlecht)" }}>{(neu.error as Error).message}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="ctl-knopf" disabled={!form.name.trim() || neu.isPending || (art === "betrag" && !betrag)} onClick={() => neu.mutate()}>Speichern</button>
            <button className="ctl-knopf" onClick={() => setForm(null)}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  )
}

function Kontenblatt({ konto, art, jahr, onClose }: { konto: Konto; art: "ertrag" | "aufwand"; jahr: number; onClose: () => void }) {
  const qc = useQueryClient()
  const einmalig = useMutation({
    mutationFn: async ({ buchung_id, id }: { buchung_id: string; id: number | null }) => {
      let r: Response
      if (id) {
        r = await fetch(`/api/v1/controlling/einmalig/${id}`, { method: "DELETE", credentials: "include" })
      } else {
        const notiz = prompt("Notiz zur einmaligen Buchung (optional), z. B. „Einrichtungsgebühr“") ?? null
        if (notiz === null) return
        r = await fetch("/api/v1/controlling/einmalig", {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ buchung_id, konto: konto.konto, notiz }),
        })
      }
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["controlling"] })
      qc.invalidateQueries({ queryKey: ["controlling-konto"] })
      qc.invalidateQueries({ queryKey: ["controlling-einmalig"] })
    },
  })
  const q = useQuery<{ zeilen: { datum: string; text: string; lieferant: string; gegenkonto: string; betrag_soll: number
    buchung_id: string; einmalig_id: number | null; einmalig_notiz: string | null }[] }>({
    queryKey: ["controlling-konto", konto.konto, jahr],
    queryFn: async () => {
      const r = await fetch(`/api/v1/controlling/konto/${encodeURIComponent(konto.konto)}?jahr=${jahr}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const summe = (q.data?.zeilen ?? []).reduce((s, z) => s + z.betrag_soll, 0)
  return (
    <>
      <div className="ctl-schleier" onClick={onClose} />
      <aside className="ctl-schublade" role="dialog" aria-label={`Kontenblatt ${konto.konto}`}>
        <div className="ctl-schublade-kopf">
          <div>
            <div className="ctl-klein">Kontenblatt {jahr} · Forecast: {konto.methode}</div>
            <h3 style={{ margin: "2px 0 0", fontSize: 16 }}>{konto.konto} {konto.name}</h3>
          </div>
          <button className="ctl-knopf" onClick={onClose} aria-label="Schließen"><X size={14} /></button>
        </div>
        <div className="ctl-schublade-inhalt">
          <Annahmen konto={konto} art={art} jahr={jahr} />
          {q.isLoading && <Loading />}
          {q.error && <ErrorBox error={q.error as Error} />}
          {q.data && (
            <table className="ctl-blatt">
              <thead><tr><th>Datum</th><th>Lieferant / Buchungstext</th><th>Gegenkonto</th><th className="zahl">Soll</th><th className="zahl">Haben</th><th /></tr></thead>
              <tbody>
                {q.data.zeilen.map((z, i) => (
                  <tr key={i}>
                    <td>{new Date(z.datum).toLocaleDateString("de-DE")}</td>
                    <td>{z.lieferant || z.text}{z.lieferant && z.text && z.text !== z.lieferant && <div className="ctl-klein">{z.text}</div>}</td><td>{z.gegenkonto}</td>
                    <td className="zahl">{z.betrag_soll > 0 ? eur2.format(z.betrag_soll) : ""}</td>
                    <td className="zahl">{z.betrag_soll < 0 ? eur2.format(-z.betrag_soll) : ""}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {z.einmalig_id ? (
                        <span className="ctl-klein" title={z.einmalig_notiz || ""}>
                          <span className="ctl-marke">einmalig</span>{" "}
                          <button className="ctl-link" onClick={() => einmalig.mutate({ buchung_id: z.buchung_id, id: z.einmalig_id })}>zurück</button>
                        </span>
                      ) : (
                        <button className="ctl-link" title="Bleibt im IST, zählt aber nicht in den Forecast"
                                onClick={() => einmalig.mutate({ buchung_id: z.buchung_id, id: null })}>einmalig</button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr><td colSpan={3} style={{ fontWeight: 600 }}>Saldo {q.data.zeilen.length} Buchungen</td>
                  <td className="zahl" style={{ fontWeight: 600 }}>{summe > 0 ? eur2.format(summe) : ""}</td>
                  <td className="zahl" style={{ fontWeight: 600 }}>{summe < 0 ? eur2.format(-summe) : ""}</td><td /></tr>
              </tbody>
            </table>
          )}
        </div>
      </aside>
    </>
  )
}
